import { useEffect, useState, useRef, useMemo, Fragment } from "react";

import { useProductStore } from "../stores/ProductStore";
import type { OutputProductsProps } from "../types/components/OutputProductsProps";
import { computeAvailableTags } from "../utilities/computeAvailableTags";
import { CategorySorting } from "./CategorySorting";
import { CategorySpecialInfo } from "./CategorySpecialInfo";
import { ProductListing } from "./ProductListing";

export const OutputProducts = ({
	products = [],
	searchProducts = [],
	prefetchedProducts = [],
	prefetchedAvailableTags = null,
	seperatorAfter = false,
	loadingAnimation = false,
	specialName = "",
	lazy = false,
	aboveFold = false,
	buyButton = false,
	description = false,
	sort = false,
}: OutputProductsProps) => {
	const [categoryProducts, setCategoryProducts] = useState(prefetchedProducts || []);
	const lastProductRef = useRef(null);
	const fetchedIdsRef = useRef<string | null>(null);

	const [activeTags, setActiveTags] = useState<Record<string, string[]>>({ colours: [], stems: [] });

	const availableTags = useMemo(() => {
		if (prefetchedAvailableTags) return prefetchedAvailableTags;
		const source = categoryProducts.length ? categoryProducts : searchProducts;
		return computeAvailableTags(source);
	}, [prefetchedAvailableTags, categoryProducts, searchProducts]);

	const GetProductInfoBulk = useProductStore.getState().GetProductInfoBulk;
	useEffect(() => {
		if (prefetchedProducts?.length) {
			setCategoryProducts(prefetchedProducts);
			return;
		}
		if (!products?.length) return;
		const productIds = products
			.map((product) => {
				if (!product?.products_id) return null;
				return product.products_id;
			})
			.filter(Boolean);
		if (!productIds.length) return;

		// Prevent re-fetching the same products
		const idsKey = productIds.join(",");
		if (fetchedIdsRef.current === idsKey) return;
		fetchedIdsRef.current = idsKey;

		const GetCategoryProducts = async (ids) => {
			const res = await GetProductInfoBulk(ids);
			const sortedProducts = productIds.map((id) =>
				res.find((product) => (product.products_id ?? product.id) === id),
			);
			setCategoryProducts(sortedProducts);
		};
		GetCategoryProducts(productIds);
	}, [products, prefetchedProducts]);

	if (searchProducts.length) {
		return (
			<>
				{sort && (
					<CategorySorting
						availableTags={availableTags}
						activeTags={activeTags}
						setActiveTags={setActiveTags}
					/>
				)}
				<div className="row">
					{searchProducts.map((product, index) => {
						if (!product?.products_id) return null;

						return (
							<Fragment key={index}>
								<ProductListing
									key={index}
									id={product.products_id}
									info={product}
									size={{ base: 6, md: 3 }}
									sort={index}
									buyButton={buyButton}
									description={description}
									tags={activeTags}
								/>
								{seperatorAfter !== false && index === seperatorAfter - 1 && <hr />}
							</Fragment>
						);
					})}
				</div>
			</>
		);
	}

	return categoryProducts.length ? (
		<>
			{sort && (
				<CategorySorting availableTags={availableTags} activeTags={activeTags} setActiveTags={setActiveTags} />
			)}
			<div className="row justify-content-space-between justify-content-md-start">
				{categoryProducts
					? categoryProducts.map((product, index) => {
							const isLastProduct = index === categoryProducts.length - 1;

							return product?.products_id ? (
								<Fragment key={index}>
									<ProductListing
										ref={isLastProduct ? lastProductRef : null}
										key={index}
										sort={index}
										id={product.products_id}
										info={product}
										size={{ base: 6, md: 3 }}
										lazy={aboveFold ? (lazy && index > 3 ? true : false) : lazy}
										buyButton={buyButton}
										description={description}
										tags={activeTags}
									/>
									{seperatorAfter !== false && index === seperatorAfter - 1 && <hr />}
								</Fragment>
							) : null;
						})
					: false}
				<CategorySpecialInfo specialName={specialName} />
			</div>
		</>
	) : products && products.length ? (
		<>
			{sort && (
				<CategorySorting availableTags={availableTags} activeTags={activeTags} setActiveTags={setActiveTags} />
			)}
			<div className="row">
				{products.map((product, index) => {
					if (!product?.products_id) return null;

					return (
						<Fragment key={index}>
							<ProductListing
								bogus={false}
								id={index}
								size={{ base: 6, md: 3 }}
								tags={activeTags}
								sort={index}
								buyButton={buyButton}
								description={description}
							/>
							{seperatorAfter !== false && index === seperatorAfter - 1 && <hr />}
						</Fragment>
					);
				})}
			</div>
		</>
	) : null;
};
